Ext.define('SA.data.MapView', {
    alias: 'view.map',
    extend: 'SA.data.View',

    mixins: {
        filterable: 'SA.data.mixin.Filterable',
        locations: 'SA.data.mixin.OneLocation'
    },

    BASE_SCALE: 2.95828763795777e8,

    statics: {
        create({project, censusRelease, name, locationItems, attributes, businessSearch}) {
            var data = {
                type: 'map',
                buildingBlocksCensusRelease: censusRelease,
                businessSearchMarkers: Ext.Array.from(businessSearch),
                choroplethAttribute: attributes.filter(id => this.supportsAttribute(id))[0] || null,
                filterVisibility: 'strikeout',
                locationItem: locationItems[0] || null,
                name
            };

            return new SA.data.MapView({project, data});
        },

        supportsAttribute(attribute) {
            return SA.data.AttributesCache.isPolygonAttribute(attribute);
        }
    },

    highlightLocation: true,
    legendVisible: true,
    locationProperty: 'locationItem',

    addAttribute(attribute) {
        var unit = this.getGeographicUnit(),
            legends = this.legends;

        if (attribute) {
            this.project.addAttribute(attribute);

            if (legends) {
                legends.setAttribute(attribute);
            }
        }

        if (this.set('choroplethAttribute', attribute)) {
            this.auditFilter();

            this.syncGeographicUnits();

            this.fireEvent('change:attribute', this);

            if (this.isAutoSelectGeoUnit() && this.getGeographicUnit() !== unit) {
                this.fireEvent('change:geographicUnit', this);
            }

            this.fireEvent('change:legend', this);

            return true;
        }

        return null;
    },

    attributeLacksUnitsAtCurrentScale() {
        var hasAttribute = this.hasAttribute(),
            attributeLoaded = hasAttribute && !!SA.data.AttributesCache.get(this.getAttribute());

        if (!this.hasScale() || !hasAttribute || !attributeLoaded) {
            return false;
        }

        return !this.getAllowedGeographicUnits().length;
    },

    createController() {
        return new SA.VectorMapController(this);
    },

    findAutoGeographicUnit() {
        var units = this.getAllowedGeographicUnits(),
            scale = this.getScale(),
            unit = units[units.length - 1];

        units.forEach(geoUnit => {
            var max = geoUnit.autoScaleMaximum;

            if (max != null && scale <= max) {
                unit = geoUnit;
            }
        });

        return unit;
    },

    getAllowedGeographicUnits() {
        var attributeId = this.getAttribute(),
            scale = this.getScale(),
            discoverableOrSelectedUnits = this.getDiscoverableOrSelectedGeoUnits(),
            units;

        if (scale == null) {
            units = [];
        }
        else if (attributeId) {
            let attribute = SA.data.AttributesCache.get(attributeId),
                unitsInCensusRelease = SA.data.GeographicUnits.idsForCountryAndCensusRelease(attribute.country, attribute.censusRelease),
                isInCensusRelease = unit => unitsInCensusRelease.includes(unit.geographicUnit);

            units = SA.data.GeographicUnits.get(attribute.geographicUnits).filter(isInCensusRelease);
        }
        else {
            units = SA.data.GeographicUnits.forCountryAndLatestCensusRelease(this.getCountry());
        }

        units = units.filter(unit => discoverableOrSelectedUnits.includes(unit.geographicUnit));

        Ext.Array.removeBy(units, unit => !isAllowedForScale(unit, scale));

        return units;

        function isAllowedForScale(unit, scale) {
            var maxScale = unit.maximumScale;

            return maxScale == null || scale <= maxScale;
        }
    },

    getAttribute() {
        return this.data.choroplethAttribute;
    },

    getAttributes() {
        var attribute = this.getAttribute();

        return attribute ? [attribute] : [];
    },

    getBusinessSearch() {
        return this.getBusinessSearches()[0] ?? null;
    },

    getBusinessSearches() {
        return this.data.businessSearchMarkers;
    },

    getDiscoverableOrSelectedGeoUnits() {
        var {clean, unique} = Ext.Array;

        return clean(unique([this.data.geographicUnit, ...this.getDiscoverableGeoUnits()]));
    },

    getExtent() {
        return this.hasExtent() ? SA.VectorMapExtent.fromXYBounds(this.data.extent) : null;
    },

    getFilterObject() {
        return this.isFiltered() ? SA.FilterExpressions.toFilter(this.getFilter()) : null;
    },

    getFilterableAttributes() {
        return this.getSupportedProjectAttributes();
    },

    getGeographicRelease() {
        var attribute = this.getAttribute(),
            location = this.getLocation();

        if (attribute) {
            return SA.data.AttributesCache.get(attribute).geographicRelease;
        }
        else if (location) {
            return SA.data.LocationsCache.getLatestGeographicRelease(location);
        }

        return SA.Country.get(this.getCountry()).getLatestGeographicRelease();
    },

    getGeographicUnit() {
        var unit = this.data.geographicUnit;

        if (!unit) {
            unit = this.findAutoGeographicUnit();

            if (unit) {
                unit = unit.id;
            }
        }

        return unit;
    },

    getInfoLocation() {
        return SA.data.LocationsCache.normalizeLocation(this.infoLocation);
    },

    getLegendBreaks() {
        var legends = this.getLegendsCache(this.getLegendBreaks, this);

        return legends && legends.getBreaks(this.getGeographicUnit());
    },

    getLegendSettings() {
        var legends = this.getLegendsCache(this.getLegendSettings, this);

        return legends && legends.getSettings(this.getGeographicUnit());
    },

    getLegendsCache(fn, scope) {
        var attribute = this.getAttribute(),
            legends = this.legends,
            id = this.getId();

        if (!attribute || !this.syncGeographicUnits()) {
            return;
        }

        if (!id) {
            if (fn) {
                this.on('save', fn, scope, {single: true});
            }

            return false;
        }

        if (!legends) {
            legends = this.legends = new SA.data.Legends({attribute, map: this});
        }

        return legends;
    },

    getMaxExtent() {
        if (this.getCountryModel().isUSA()) {
            return SA.VectorMapExtent.fromXYBounds([-20000000, -2000000, 2500000, 14000000]);
        }
        else {
            return SA.VectorMapExtent.fromXYBounds([-20000000, 0, 2500000, 14000000]);
        }
    },

    getQueryLocation() {
        return SA.data.LocationsCache.normalizeLocation(this.getLocation());
    },

    getScale() {
        return this.scale;
    },

    getState() {
        return SA.VectorMapModelState.get(this);
    },

    getZoom() {
        return Math.log2(this.BASE_SCALE / this.getScale());
    },

    hasAttribute() {
        return !!this.getAttribute();
    },

    hasAttributeWithoutUnitsAtCurrentScale() {
        return this.hasAttribute() && this.attributeLacksUnitsAtCurrentScale();
    },

    hasAttributeWithUnitsAtCurrentScale() {
        return this.hasAttribute() && !this.attributeLacksUnitsAtCurrentScale();
    },

    hasBusinessSearch() {
        return !!this.getBusinessSearch();
    },

    hasExtent() {
        return !!this.data.extent;
    },

    hasLabels() {
        return !!this.labels;
    },

    hasLackOfDataVariation() {
        return this.getLegendBreaks() === null;
    },

    hasLocationMask() {
        return !!this.locationMask;
    },

    hasScale() {
        return Ext.isNumber(this.getScale());
    },

    hasUserAttribute() {
        return this.hasAttribute() && SA.data.UserAttributes.isUserAttribute(this.getAttribute());
    },

    hasUserAttributeWithoutUnitsAtCurrentScale() {
        return this.hasUserAttribute() && this.hasAttributeWithoutUnitsAtCurrentScale();
    },

    isAutoSelectGeoUnit() {
        return this.data.geographicUnit == null;
    },

    isHighlightLocation() {
        return !!this.highlightLocation;
    },

    isLegendLoading() {
        if (!this.hasAttributeWithUnitsAtCurrentScale()) {
            return false;
        }

        return !this.legends || this.legends.isLoading();
    },

    isLegendVisible() {
        return !!this.legendVisible;
    },

    isReleaseVisible() {
        return !!this.getCensusRelease() && this.getCensusRelease() !== this.getProject().getLatestCensusRelease();
    },

    isSavable() {
        return this.hasExtent();
    },

    isViewable() {
        return true;
    },

    setBusinessSearch(search) {
        var data = this.data,
            oldSearch = data.businessSearchMarkers[0];

        if (search !== oldSearch) {
            data.businessSearchMarkers = search ? [search]: [];

            this.fireEvent('change:businessSearch', this);
        }
    },

    setExtent(extent, {suppressEvent = false} = {}) {
        var oldExtent = this.getExtent();

        this.data.extent = extent.toXYBounds();

        if (!suppressEvent && !extent.equals(oldExtent)) {
            this.fireEvent('change:extent');
        }
    },

    setGeographicUnit(geographicUnit) {
        if (this.setWithEvent('geographicUnit', geographicUnit)) {
            this.fireEvent('change:legend', this);
        }
    },

    setHighlightLocation(highlightLocation) {
        this.setPropertyWithEvent('highlightLocation', highlightLocation);
    },

    setInfoLocation(location) {
        this.setPropertyWithEvent('infoLocation', SA.data.LocationsCache.toLocationSeries(location));
    },

    setLabels(labels) {
        this.setPropertyWithEvent('labels', labels);
    },

    setLegend(properties) {
        var legends = this.legends;

        return legends.set(Object.assign({
            attribute: this.getAttribute(),
            geographicUnit: this.getGeographicUnit()
        }, properties));
    },

    setLegendVisible(legendVisible) {
        this.setPropertyWithEvent('legendVisible', legendVisible);
    },

    setLocationImpl(location) {
        this.mixins.locations.setLocationImpl.call(this, location);

        if (location) {
            this.project.addLocation(location);
        }
    },

    setLocationMask(locationMask) {
        this.setPropertyWithEvent('locationMask', locationMask);
    },

    setPropertyWithEvent(property, value) {
        if (this[property] !== value) {
            this[property] = value;
            this.fireEvent('change:' + property, this);
        }
    },

    setScale(scale) {
        var unit = this.getGeographicUnit(),
            changed = this.scale !== scale;

        this.scale = scale;

        this.syncGeographicUnits();

        if (this.isAutoSelectGeoUnit() && this.getGeographicUnit() !== unit) {
            this.fireEvent('change:geographicUnit', this);
            this.fireEvent('change:legend', this);
        }

        if (changed) {
            this.fireEvent('change:scale', this);
        }
    },

    setZoom(zoom) {
        this.setScale(this.BASE_SCALE / 2 ** zoom);
    },

    syncGeographicUnits() {
        var allowedUnits = Ext.Array.pluck(this.getAllowedGeographicUnits(), 'id');

        if (!allowedUnits.length) {
            return false;
        }

        if (!this.isAutoSelectGeoUnit() && !allowedUnits.includes(this.getGeographicUnit())) {
            this.setGeographicUnit(allowedUnits[allowedUnits.length - 1]);
        }

        return true;
    },

    toExportData() {
        return {
            labels: this.hasLabels(),
            landLayer: 'full',
            locationHighlight: !!(this.isHighlightLocation() && this.getLocation()),
            locationMask: this.hasLocationMask(),

            scale: this.getScale()
        };
    }
});